import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiCheck, FiChevronDown } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useBranch } from '../../context/BranchContext';

const CurrencySwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const { currencies, selectedCurrency, setSelectedCurrency, isLoadingCurrencies } = useBranch() as any;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (id: string) => {
    setSelectedCurrency(id);
    setIsOpen(false);
  };

  const label = selectedCurrency
    ? ` ${selectedCurrency.iso}`
    : '—';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center cursor-pointer gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-expanded={isOpen}
        aria-haspopup="true"
        disabled={isLoadingCurrencies}
        title={selectedCurrency ? `${selectedCurrency.name}` : 'Select currency'}
      >
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {isLoadingCurrencies ? '...' : label}
        </span>
        <FiChevronDown className={`w-3.5 h-3.5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 ${isRTL ? 'left-0' : 'right-0'} mt-2 w-52 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden`}
          >
            <div className="p-1.5 max-h-72 overflow-auto" role="menu" aria-orientation="vertical">
              {currencies.length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {isLoadingCurrencies ? 'Loading…' : 'No currencies'}
                </div>
              )}
              {currencies.map((c: any) => {
                const isActive = selectedCurrency?.id === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c.id)}
                    className={`w-full cursor-pointer text-start px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all duration-150 border ${isActive
                      ? 'bg-gray-100 dark:bg-gray-700 border-primary'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 border-transparent'
                    }`}
                    role="menuitem"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-lg font-medium text-gray-700 dark:text-gray-200">
                      {c.symbol ?? '¤'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium block truncate ${isActive ? 'text-primary' : 'text-gray-800 dark:text-gray-100'}`}>
                        {c.name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{c.iso}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CurrencySwitcher;
